import { vec3 } from 'gl-matrix';
import { buildPlanarActorEntry } from './buildPlanarActorEntry';
import CanvasActor from '../../CanvasActor';
import calculateTransform from '../../helpers/cpuFallback/rendering/calculateTransform';
import canvasToPixel from '../../helpers/cpuFallback/rendering/canvasToPixel';
import correctShift from '../../helpers/cpuFallback/rendering/correctShift';
import getDefaultViewport from '../../helpers/cpuFallback/rendering/getDefaultViewport';
import { getDefaultImageVOIRange } from '../../helpers/planarImageRendering';
import resizeEnabledElement from '../../helpers/cpuFallback/rendering/resize';
import drawImageSync from '../../helpers/cpuFallback/drawImageSync';
import { resolveCPUFallbackColormap } from '../../helpers/cpuFallback/colors';
import {
  InterpolationType,
  MetadataModules,
  ViewportType,
  Events,
  ViewportStatus,
} from '../../../enums';
import { loadAndCacheImage } from '../../../loaders/imageLoader';
import * as metaData from '../../../metaData';
import { ActorRenderMode } from '../../../types';
import { getImageDataMetadata } from '../../../utilities/getImageDataMetadata';
import triggerEvent from '../../../utilities/triggerEvent';
import uuidv4 from '../../../utilities/uuidv4';
import { toWindowLevel } from '../../../utilities/windowLevel';
import type {
  CPUIImageData,
  CPUFallbackEnabledElement,
  IImage,
  Point2,
  Point3,
} from '../../../types';
import type { IViewport } from '../../../types/IViewport';
import type {
  DataAddOptions,
  LoadedData,
  RenderPathAttachment,
  RenderPathDefinition,
  RenderPath,
} from '../ViewportArchitectureTypes';
import type {
  PlanarViewState,
  PlanarDataPresentation,
  PlanarCpuImageAdapterContext,
  PlanarPayload,
  PlanarResolvedICamera,
  PlanarViewportRenderContext,
} from './PlanarViewportTypes';
import type { PlanarCpuImageRendering } from './planarRuntimeTypes';
import type { DerivedPlanarPresentation } from './planarRenderCamera';
import {
  resolvePlanarCpuImageDisplayedArea,
  resolvePlanarCpuViewportScale,
} from './planarCpuViewportMath';
import { triggerPlanarNewImage } from './planarImageEvents';
import {
  canvasToWorldPlanarRenderPathProjection,
  resolvePlanarRenderPathCurrentImageIdIndex,
  resolvePlanarRenderPathProjection,
  worldToCanvasPlanarRenderPathProjection,
} from './planarRenderPathProjection';

/** @internal */
export class CpuImageSliceRenderPath
  implements RenderPath<PlanarCpuImageAdapterContext>
{
  async addData(
    ctx: PlanarCpuImageAdapterContext,
    data: LoadedData,
    options: DataAddOptions
  ): Promise<RenderPathAttachment<PlanarDataPresentation>> {
    const payload: PlanarPayload = data as unknown as LoadedData<PlanarPayload>;

    if (!payload.image) {
      throw new Error('[PlanarViewport] CPU rendering requires an image');
    }

    ctx.display.activateRenderMode(ActorRenderMode.CPU_IMAGE);

    let rendering: PlanarCpuImageRendering;
    markCpuImagePreScaled(payload.image);
    const compatibilityActor = new CanvasActor(
      {
        getImageData: () => this.getImageData(rendering),
      } as unknown as IViewport,
      payload.image
    );

    compatibilityActor.setVisibility(true);

    const defaultViewport = getDefaultViewport(
      ctx.cpu.canvas,
      payload.image,
      getCPUFallbackViewportModality(payload.image)
    );

    defaultViewport.displayedArea = resolvePlanarCpuImageDisplayedArea(
      payload.image
    );

    const enabledElement = {
      canvas: ctx.cpu.canvas,
      image: payload.image,
      renderingTools: {},
      viewport: defaultViewport,
    } as CPUFallbackEnabledElement;

    resizeEnabledElement(enabledElement, true);
    enabledElement.transform = calculateTransform(enabledElement);
    rendering = {
      renderMode: ActorRenderMode.CPU_IMAGE,
      actorEntryUID: uuidv4(),
      enabledElement,
      compatibilityActor,
      currentImageIdIndex: payload.initialImageIdIndex ?? 0,
      defaultVOIRange: getDefaultImageVOIRange(payload.image),
      dataPresentation: undefined,
      fitScale: getCPUFallbackScalarScale(enabledElement.viewport.scale),
      loadRequestId: 0,
      renderingInvalidated: true,
    };

    triggerPlanarNewImage(ctx, {
      image: payload.image,
      imageIdIndex: payload.initialImageIdIndex ?? 0,
    });

    return {
      rendering,
      updateDataPresentation: (props) => {
        this.updateDataPresentation(rendering, props);
      },
      applyViewState: (camera) => {
        this.applyViewState(ctx, rendering, data.id, camera, payload.imageIds);
      },
      getFrameOfReferenceUID: () => {
        return this.getFrameOfReferenceUID(rendering);
      },
      getActorEntry: (data) => {
        return buildPlanarActorEntry(data as LoadedData<PlanarPayload>, {
          actor: rendering.compatibilityActor,
          mapper: rendering.compatibilityActor.getMapper(),
          renderMode: ActorRenderMode.CPU_IMAGE,
          uid: rendering.actorEntryUID,
          referencedIdFallback: rendering.enabledElement.image?.imageId,
        });
      },
      getImageData: () => {
        return this.getImageData(rendering);
      },
      render: () => {
        this.render(ctx, rendering, data.id);
      },
      resize: () => {
        this.resize(ctx, rendering, data.id);
      },
      removeData: () => {
        this.removeData(ctx, rendering);
      },
    };
  }

  private updateDataPresentation(
    rendering: PlanarCpuImageRendering,
    props: unknown
  ): void {
    rendering.dataPresentation = props as PlanarDataPresentation | undefined;
    applyDataPresentation(rendering, rendering.dataPresentation);
  }

  private applyViewState(
    ctx: PlanarCpuImageAdapterContext,
    rendering: PlanarCpuImageRendering,
    dataId: string,
    camera: unknown,
    imageIds: string[]
  ): void {
    const planarCamera = camera as PlanarViewState | undefined;
    const image = rendering.enabledElement.image;

    ctx.display.activateRenderMode(ActorRenderMode.CPU_IMAGE);

    const projection = image
      ? resolvePlanarRenderPathProjection({
          ctx,
          dataId,
          imageIds,
          rendering,
          viewState: planarCamera,
        })
      : undefined;
    const nextImageIdIndex = resolvePlanarRenderPathCurrentImageIdIndex({
      projection,
      rendering,
      viewState: planarCamera,
    });

    if (projection) {
      applyPresentationState(
        rendering,
        projection.presentation,
        projection.resolvedICamera
      );
    }

    // See VtkImageMapperRenderPath for context on why this dedupes against
    // lastRequestedImageIdIndex instead of currentImageIdIndex.
    const dedupTarget =
      rendering.lastRequestedImageIdIndex ?? rendering.currentImageIdIndex;

    if (nextImageIdIndex === dedupTarget) {
      return;
    }

    if (nextImageIdIndex < 0 || nextImageIdIndex >= imageIds.length) {
      return;
    }

    rendering.lastRequestedImageIdIndex = nextImageIdIndex;
    const requestId = ++rendering.loadRequestId;

    void loadAndCacheImage(imageIds[nextImageIdIndex]).then((image) => {
      if (requestId !== rendering.loadRequestId) {
        return;
      }

      void updateRenderedImage({
        ctx,
        dataId,
        image,
        imageIdIndex: nextImageIdIndex,
        props: rendering.dataPresentation,
        rendering,
      });
    });
  }

  private canvasToWorld(
    ctx: PlanarCpuImageAdapterContext,
    rendering: PlanarCpuImageRendering,
    canvasPos: Point2
  ): Point3 {
    return canvasToWorldPlanarRenderPathProjection({
      canvas: ctx.cpu.canvas,
      canvasPos,
      ctx,
    });
  }

  private worldToCanvas(
    ctx: PlanarCpuImageAdapterContext,
    rendering: PlanarCpuImageRendering,
    worldPos: Point3
  ): Point2 {
    return worldToCanvasPlanarRenderPathProjection({
      canvas: ctx.cpu.canvas,
      ctx,
      worldPos,
    });
  }

  private getFrameOfReferenceUID(
    rendering: PlanarCpuImageRendering
  ): string | undefined {
    const imageId = rendering.enabledElement.image?.imageId;
    const imagePlaneModule = imageId
      ? (metaData.get(MetadataModules.IMAGE_PLANE, imageId) as
          | { frameOfReferenceUID?: string }
          | undefined)
      : undefined;

    return imagePlaneModule?.frameOfReferenceUID;
  }

  private getImageData(
    rendering: PlanarCpuImageRendering
  ): CPUIImageData | undefined {
    const image = rendering.enabledElement.image;

    if (!image) {
      return;
    }

    return buildPlanarImageData(image, this.getFrameOfReferenceUID(rendering));
  }

  private render(
    ctx: PlanarCpuImageAdapterContext,
    rendering: PlanarCpuImageRendering,
    dataId: string
  ): void {
    if (!ctx.viewport.isCurrentDataId(dataId)) {
      return;
    }

    renderCPUImage(rendering);
    renderCompatibilityOverlayActors(ctx);
    ctx.display.markRendered();
    triggerEvent(ctx.viewport.element, Events.IMAGE_RENDERED, {
      element: ctx.viewport.element,
      viewportId: ctx.viewportId,
      renderingEngineId: ctx.renderingEngineId,
      viewportStatus: ViewportStatus.RENDERED,
    });
  }

  private resize(
    ctx: PlanarCpuImageAdapterContext,
    rendering: PlanarCpuImageRendering,
    dataId: string
  ): void {
    resizeEnabledElement(rendering.enabledElement, true);
    const image = rendering.enabledElement.image;
    const camera = ctx.viewport.getViewState();

    if (!image) {
      return;
    }

    const projection = resolvePlanarRenderPathProjection({
      ctx,
      dataId,
      rendering,
      viewState: camera,
    });

    rendering.fitScale = getCPUFallbackScalarScale(
      getDefaultViewport(
        rendering.enabledElement.canvas,
        image,
        getCPUFallbackViewportModality(image)
      ).scale
    );
    rendering.renderingInvalidated = true;

    if (projection) {
      applyPresentationState(
        rendering,
        projection.presentation,
        projection.resolvedICamera
      );
    }
  }

  private removeData(
    ctx: PlanarCpuImageAdapterContext,
    rendering: PlanarCpuImageRendering
  ): void {
    const { enabledElement } = rendering;

    ctx.cpu.context.setTransform(1, 0, 0, 1, 0, 0);
    ctx.cpu.context.clearRect(
      0,
      0,
      ctx.cpu.canvas.width,
      ctx.cpu.canvas.height
    );
    enabledElement.image = undefined;
  }
}

/** @internal */
export class CpuImageSlicePath
  implements
    RenderPathDefinition<
      PlanarViewportRenderContext,
      PlanarCpuImageAdapterContext
    >
{
  readonly id = 'planar:cpu-image-slice';
  readonly type = ViewportType.PLANAR_NEXT;

  matches(data: LoadedData, options: DataAddOptions): boolean {
    return (
      data.type === 'image' && options.renderMode === ActorRenderMode.CPU_IMAGE
    );
  }

  createRenderPath() {
    return new CpuImageSliceRenderPath();
  }

  selectContext(
    rootContext: PlanarViewportRenderContext
  ): PlanarCpuImageAdapterContext {
    return {
      viewportId: rootContext.viewportId,
      renderingEngineId: rootContext.renderingEngineId,
      type: rootContext.type,
      viewport: rootContext.viewport,
      renderPath: rootContext.renderPath,
      view: rootContext.view,
      display: rootContext.display,
      cpu: rootContext.cpu,
    };
  }
}

function applyDataPresentation(
  rendering: PlanarCpuImageRendering,
  props?: PlanarDataPresentation
): void {
  const { enabledElement, defaultVOIRange } = rendering;
  const { viewport } = enabledElement;
  const canvas = enabledElement.canvas as HTMLCanvasElement;
  const voiRange = props?.voiRange ?? defaultVOIRange;

  canvas.style.display = props?.visible === false ? 'none' : '';
  canvas.style.opacity = String(props?.opacity ?? 1);

  viewport.colormap = resolveCPUFallbackColormap(
    props?.colormap,
    enabledElement.image?.colormap
  );
  viewport.invert = props?.invert ?? false;

  if (voiRange) {
    const { windowCenter, windowWidth } = toWindowLevel(
      voiRange.lower,
      voiRange.upper
    );

    viewport.voi = {
      windowCenter,
      windowWidth,
      voiLUTFunction: enabledElement.image?.voiLUTFunction,
    };
  }

  rendering.renderingInvalidated = true;

  if (props?.interpolationType !== undefined) {
    viewport.pixelReplication =
      props.interpolationType !== InterpolationType.LINEAR;
  }
}

function getCPUFallbackScalarScale(scale?: number | Point2): number {
  return Array.isArray(scale) ? scale[1] : (scale ?? 1);
}

function applyPresentationState(
  rendering: PlanarCpuImageRendering,
  presentation?: DerivedPlanarPresentation,
  activeSourceICamera?: PlanarResolvedICamera
): void {
  const { enabledElement, fitScale } = rendering;
  const viewport = enabledElement.viewport as Omit<
    CPUFallbackEnabledElement['viewport'],
    'scale'
  > & {
    scale?: number | Point2;
  };
  const desiredPan = presentation?.pan ?? [0, 0];
  const zoom = Math.max(presentation?.zoom ?? 1, 0.001);

  viewport.hflip = presentation?.flipHorizontal ?? false;
  viewport.vflip = presentation?.flipVertical ?? false;
  viewport.rotation = presentation?.rotation ?? 0;
  viewport.scale =
    typeof activeSourceICamera?.parallelScale === 'number'
      ? resolvePlanarCpuViewportScale({
          canvas: enabledElement.canvas,
          parallelScale: activeSourceICamera.parallelScale,
          presentationScale: activeSourceICamera.presentationScale,
          columnPixelSpacing: enabledElement.image?.columnPixelSpacing || 1,
          rowPixelSpacing: enabledElement.image?.rowPixelSpacing || 1,
        })
      : [fitScale * (presentation?.scale?.[0] ?? zoom), fitScale * zoom];
  viewport.parallelScale = activeSourceICamera?.parallelScale;
  viewport.translation = activeSourceICamera?.focalPoint
    ? resolveCPUImageViewportTranslationFromFocalPoint(
        enabledElement,
        activeSourceICamera.focalPoint
      )
    : resolveCPUImageViewportTranslation(enabledElement, desiredPan);

  enabledElement.transform = calculateTransform(enabledElement);
}

function resolveCPUImageViewportTranslationFromFocalPoint(
  enabledElement: CPUFallbackEnabledElement,
  focalPoint: Point3
): { x: number; y: number } {
  const { image, viewport } = enabledElement;
  const originalTranslation = viewport.translation || { x: 0, y: 0 };

  if (!image) {
    return originalTranslation;
  }

  viewport.translation = { x: 0, y: 0 };
  enabledElement.transform = calculateTransform(enabledElement);

  const referencePoint = canvasToPixel(enabledElement, [
    enabledElement.canvas.width / 2,
    enabledElement.canvas.height / 2,
  ]);
  const targetPoint = worldToCPUImageDrawPoint(enabledElement, focalPoint);
  const shift = correctShift(
    {
      x: targetPoint[0] - referencePoint[0],
      y: targetPoint[1] - referencePoint[1],
    },
    viewport
  );

  viewport.translation = originalTranslation;

  return {
    x: -shift.x,
    y: -shift.y,
  };
}

function resolveCPUImageViewportTranslation(
  enabledElement: CPUFallbackEnabledElement,
  desiredPan: Point2
): { x: number; y: number } {
  const { viewport } = enabledElement;
  const originalTranslation = viewport.translation || { x: 0, y: 0 };

  viewport.translation = { x: 0, y: 0 };
  const baseOrigin = calculateTransform(enabledElement).transformPoint([0, 0]);

  viewport.translation = { x: 1, y: 0 };
  const translatedXOrigin = calculateTransform(enabledElement).transformPoint([
    0, 0,
  ]);

  viewport.translation = { x: 0, y: 1 };
  const translatedYOrigin = calculateTransform(enabledElement).transformPoint([
    0, 0,
  ]);

  viewport.translation = originalTranslation;

  const deltaX: Point2 = [
    translatedXOrigin[0] - baseOrigin[0],
    translatedXOrigin[1] - baseOrigin[1],
  ];
  const deltaY: Point2 = [
    translatedYOrigin[0] - baseOrigin[0],
    translatedYOrigin[1] - baseOrigin[1],
  ];
  const determinant = deltaX[0] * deltaY[1] - deltaX[1] * deltaY[0];

  if (Math.abs(determinant) < 1e-6) {
    return { x: 0, y: 0 };
  }

  return {
    x: (desiredPan[0] * deltaY[1] - desiredPan[1] * deltaY[0]) / determinant,
    y: (deltaX[0] * desiredPan[1] - deltaX[1] * desiredPan[0]) / determinant,
  };
}

function worldToCPUImageDrawPoint(
  enabledElement: CPUFallbackEnabledElement,
  worldPos: Point3
): Point2 {
  const { image, viewport } = enabledElement;

  if (!image) {
    return [0, 0];
  }

  const { spacing, direction, origin } = getImageDataMetadata(image);
  const rowVector = direction.slice(0, 3) as Point3;
  const columnVector = direction.slice(3, 6) as Point3;
  const displayedArea = viewport.displayedArea;
  const sourceX = (displayedArea?.tlhc.x ?? 1) - 1;
  const sourceY = (displayedArea?.tlhc.y ?? 1) - 1;
  const diff = vec3.subtract(
    vec3.create(),
    worldPos as unknown as vec3,
    origin as unknown as vec3
  );

  return [
    vec3.dot(diff, rowVector as unknown as vec3) / spacing[0] + 0.5 - sourceX,
    vec3.dot(diff, columnVector as unknown as vec3) / spacing[1] +
      0.5 -
      sourceY,
  ];
}

/**
 * Mirror legacy StackViewport._setCSImage: when the cached image's pixel data is
 * already pre-scaled (modality rescale baked in), flag it so the CPUFallback
 * renderer skips the modality LUT. Without this the renderer
 * (generateLut/getDefaultViewport/renderGrayscaleImage all branch on
 * image.isPreScaled) re-applies slope/intercept on top of already-scaled data,
 * shifting every pixel down by the rescale intercept — e.g. a CT renders far too
 * dark, with only the densest bone surviving the window.
 */
function markCpuImagePreScaled(image: IImage): void {
  image.isPreScaled = image.preScale?.scaled;
}

function getCPUFallbackViewportModality(image: IImage): string | undefined {
  return (
    image.preScale?.scalingParameters?.modality ||
    (
      metaData.get(MetadataModules.GENERAL_SERIES, image.imageId) as
        | { modality?: string }
        | undefined
    )?.modality
  );
}

export function buildPlanarImageData(
  image: IImage,
  frameOfReferenceUID?: string
): CPUIImageData {
  const metadata = getImageDataMetadata(image);
  const { dimensions, direction, modality, origin, spacing } = metadata;
  // getImageDataMetadata().calibration is the DICOM calibration module; the USER
  // calibration (CalibrationLine -> calibratedPixelSpacing provider) is stored
  // separately, so merge it on top — mirroring legacy StackViewport.getImageData()
  // (`calibration: { ...csImage.calibration, ...this.calibration }`). Without this,
  // native getImageData().calibration ignores user calibration and length tools never
  // rescale after calibrateImageSpacing().
  const userCalibration = metaData.get(
    'calibratedPixelSpacing',
    image.imageId
  ) as Record<string, unknown> | undefined;
  const calibration = userCalibration
    ? { ...metadata.calibration, ...userCalibration }
    : metadata.calibration;
  const rowVector = direction.slice(0, 3) as Point3;
  const columnVector = direction.slice(3, 6) as Point3;
  const scalarData =
    image.voxelManager?.getScalarData() || image.getPixelData?.();

  return {
    dimensions,
    spacing,
    origin,
    direction,
    metadata: {
      Modality: modality,
      FrameOfReferenceUID: frameOfReferenceUID,
    },
    imageData: {
      getDirection: () => direction,
      getDimensions: () => dimensions,
      getScalarData: () => scalarData,
      getSpacing: () => spacing,
      worldToIndex: (point: Point3) => {
        const diff = vec3.subtract(
          vec3.create(),
          point as unknown as vec3,
          origin as unknown as vec3
        );

        return [
          vec3.dot(diff, rowVector as unknown as vec3) / spacing[0],
          vec3.dot(diff, columnVector as unknown as vec3) / spacing[1],
          0,
        ] as Point3;
      },
      indexToWorld: (point: Point3) => {
        const worldPoint = [...origin] as Point3;

        vec3.scaleAndAdd(
          worldPoint as unknown as vec3,
          worldPoint as unknown as vec3,
          rowVector as unknown as vec3,
          point[0] * spacing[0]
        );
        vec3.scaleAndAdd(
          worldPoint as unknown as vec3,
          worldPoint as unknown as vec3,
          columnVector as unknown as vec3,
          point[1] * spacing[1]
        );

        return worldPoint;
      },
    },
    scalarData,
    scaling: image.scaling,
    hasPixelSpacing: Boolean(image.rowPixelSpacing || image.columnPixelSpacing),
    calibration,
    preScale: image.preScale,
    voxelManager: image.voxelManager,
  };
}

function renderCPUImage(rendering: PlanarCpuImageRendering): void {
  const { enabledElement, renderingInvalidated } = rendering;

  if (!enabledElement.image) {
    return;
  }

  drawImageSync(enabledElement, renderingInvalidated);
  rendering.renderingInvalidated = false;
}

function renderCompatibilityOverlayActors(
  ctx: PlanarCpuImageAdapterContext
): void {
  const overlayActors = ctx.viewport.getOverlayActors();

  for (const actorEntry of overlayActors) {
    if (actorEntry.actorMapper?.renderMode !== ActorRenderMode.CPU_IMAGE) {
      continue;
    }

    (actorEntry.actor as CanvasActor).render(
      undefined as never,
      ctx.cpu.context
    );
  }
}

async function updateRenderedImage(args: {
  ctx: PlanarCpuImageAdapterContext;
  dataId: string;
  image: IImage;
  imageIdIndex: number;
  props?: PlanarDataPresentation;
  rendering: PlanarCpuImageRendering;
}): Promise<void> {
  const { ctx, dataId, image, imageIdIndex, props, rendering } = args;
  const enabledElement = rendering.enabledElement;
  markCpuImagePreScaled(image);
  const camera = ctx.viewport.getViewState();
  const defaultViewport = getDefaultViewport(
    ctx.cpu.canvas,
    image,
    getCPUFallbackViewportModality(image)
  );
  const previousViewport = enabledElement.viewport;

  defaultViewport.displayedArea = resolvePlanarCpuImageDisplayedArea(image);

  enabledElement.image = image;
  enabledElement.viewport = {
    ...defaultViewport,
    hflip: previousViewport?.hflip ?? defaultViewport.hflip,
    invert: defaultViewport.invert,
    pixelReplication:
      previousViewport?.pixelReplication ?? defaultViewport.pixelReplication,
    rotation: previousViewport?.rotation ?? defaultViewport.rotation,
    translation: previousViewport?.translation ?? defaultViewport.translation,
    vflip: previousViewport?.vflip ?? defaultViewport.vflip,
  };

  rendering.currentImageIdIndex = imageIdIndex;
  rendering.defaultVOIRange = getDefaultImageVOIRange(image);
  rendering.fitScale = getCPUFallbackScalarScale(defaultViewport.scale);
  rendering.renderingInvalidated = true;
  ctx.viewport.invalidateResolvedView();
  rendering.compatibilityActor
    .getMapper()
    .getInputData()
    .setDerivedImage(image);

  applyDataPresentation(rendering, props);
  const projection = resolvePlanarRenderPathProjection({
    ctx,
    dataId,
    rendering,
    viewState: camera,
  });

  if (projection) {
    applyPresentationState(
      rendering,
      projection.presentation,
      projection.resolvedICamera
    );
  }

  triggerPlanarNewImage(ctx, { image, imageIdIndex });
  // cpuImage is drawn by the Planar viewport itself, not by the rendering
  // engine's VTK pass. The image swap therefore needs an immediate viewport
  // render or the visible canvas stays stale until another direct render.
  ctx.display.renderNow();
}
